import { z } from 'zod';
import { ConversationCaseRoleSchema } from './toolResults.js';

/**
 * Contrats entre le serveur et les écrans (chat web, Word, volet Outlook/Gmail)
 * pour la liste des dossiers d'une conversation (spec #2592, #2598) : les gestes
 * de l'avocat portés par son message, la trame `conversation_cases` que le
 * serveur envoie après chaque changement de la liste, et la liste rendue par le
 * détail d'une conversation. Clés en snake_case, comme le reste de la trame
 * `user_message` et des lignes HTTP.
 */

/**
 * Plafond des gestes portés par un message. Un geste par action de l'avocat
 * depuis son envoi précédent : quelques-uns en pratique. La borne évite qu'un
 * client défaillant fasse prendre des centaines de verrous dans une transaction.
 */
export const CONVERSATION_CASE_GESTURES_MAX = 50;

/**
 * Un geste fait dans l'écran depuis l'envoi précédent, appliqué par le serveur
 * en début de tour, au nom de l'avocat, dans l'ordre du tableau :
 * - `add_related` : ajouter un dossier (il devient principal si la conversation
 *   n'a jamais eu de dossier ; un lien retiré redevient actif) ;
 * - `remove_related` : retirer un dossier connexe (le lien reste comme trace) ;
 * - `remove_principal` : retirer le principal que l'écran affichait
 *   (`case_id`) ; aucun connexe ne le remplace. Sans effet s'il n'est plus le
 *   principal. Le geste prime sur un `case_id` de message resté sur ce dossier.
 *
 * Changer de principal ne passe pas par ici : c'est le `case_id` du message,
 * le dossier choisi au composeur, appliqué après ces gestes.
 */
export const ConversationCaseGestureSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('add_related'), case_id: z.string().uuid() }),
  z.object({ kind: z.literal('remove_related'), case_id: z.string().uuid() }),
  z.object({ kind: z.literal('remove_principal'), case_id: z.string().uuid() }),
]);
export type ConversationCaseGesture = z.infer<typeof ConversationCaseGestureSchema>;

/** Champ facultatif `case_changes` de la trame `user_message`, dans les deux agents. */
export const ConversationCaseGesturesSchema = z.array(ConversationCaseGestureSchema).max(CONVERSATION_CASE_GESTURES_MAX);

/**
 * Qui a mis un dossier dans la liste, et quand (ISO 8601). `agent` : l'agent,
 * par un outil, avec les droits de l'avocat `user_id` dont c'était le tour.
 */
export const ConversationCaseAuthorSchema = z.object({
  kind: z.enum(['lawyer', 'agent']),
  user_id: z.string().min(1),
  at: z.string().min(1),
});
export type ConversationCaseAuthor = z.infer<typeof ConversationCaseAuthorSchema>;

/**
 * Un dossier de la liste active, tel que l'écran l'affiche. `added_by` vaut
 * `null` pour un principal d'origine dont l'auteur est inconnu (posé avant le
 * chantier, ou à la création de la conversation par HTTP).
 */
export const ConversationCaseListItemSchema = z.object({
  case_id: z.string().min(1),
  title: z.string(),
  client_name: z.string().nullable(),
  role: ConversationCaseRoleSchema,
  added_by: ConversationCaseAuthorSchema.nullable(),
});
export type ConversationCaseListItem = z.infer<typeof ConversationCaseListItemSchema>;

/** Liste active d'une conversation : le principal d'abord, puis les connexes par date d'ajout. */
export const ConversationCaseListSchema = z.array(ConversationCaseListItemSchema);

/** Un avocat qui lisait la conversation et ne la lit plus, faute d'accès à un dossier entré dans la liste. */
export const ConversationLostReaderSchema = z.object({
  user_id: z.string().min(1),
  /** Nom complet, sinon e-mail ; `null` si aucun n'est connu. */
  display_name: z.string().nullable(),
});
export type ConversationLostReader = z.infer<typeof ConversationLostReaderSchema>;

/**
 * Charge de la trame `conversation_cases`. `lost_readers` : ceux qui ont perdu
 * la conversation par CE changement (vide le plus souvent) ; l'écran avertit
 * l'avocat, l'ajout n'est pas bloqué.
 */
export const ConversationCasesPayloadSchema = z.object({
  conversation_id: z.string().min(1),
  cases: ConversationCaseListSchema,
  lost_readers: z.array(ConversationLostReaderSchema),
});
export type ConversationCasesPayload = z.infer<typeof ConversationCasesPayloadSchema>;

/**
 * Trame serveur → écran, envoyée au début de chaque tour accepté (après les
 * gestes et le dossier du composeur) et après chaque changement de la liste
 * par un outil de l'agent. Elle porte toujours la liste entière : l'écran la
 * remplace, il ne l'additionne pas.
 */
export const ConversationCasesFrameSchema = z.object({
  type: z.literal('conversation_cases'),
  timestamp: z.string(),
  payload: ConversationCasesPayloadSchema,
});
export type ConversationCasesFrame = z.infer<typeof ConversationCasesFrameSchema>;

/**
 * Ce que `GET /api/conversations/:conversationId` ajoute à la ligne de la
 * conversation : sa liste active, pour la retrouver à la réouverture, dans le
 * web, dans Word ou dans le volet.
 */
export const ConversationDetailCasesSchema = z.object({
  conversation_cases: ConversationCaseListSchema,
});
export type ConversationDetailCases = z.infer<typeof ConversationDetailCasesSchema>;

/**
 * Codes de l'`error` du serveur qui refusent un message à cause des dossiers de
 * la conversation (`conversation-case-scope/ConversationCaseScope.ts`) : rien n'a
 * été appliqué ni lancé, l'écran relit la liste réelle. Une copie, pour les
 * écrans qui les écoutent et pour le type de la charge côté serveur.
 */
export const CONVERSATION_CASE_REFUSAL_CODES = [
  'case_change_refused',
  'principal_change_refused',
  'case_access_denied',
  'principal_case_unreadable',
  'invalid_case_changes',
] as const;
export type ConversationCaseRefusalCode = (typeof CONVERSATION_CASE_REFUSAL_CODES)[number];

/** Vrai quand `code` (champ libre d'une trame `error`) est un refus lié aux dossiers. */
export function isConversationCaseRefusalCode(code: unknown): code is ConversationCaseRefusalCode {
  return typeof code === 'string' && (CONVERSATION_CASE_REFUSAL_CODES as readonly string[]).includes(code);
}

/** Un refus de message lié aux dossiers, tel que les trois écrans le lisent. */
export interface ConversationCaseRefusal {
  code: ConversationCaseRefusalCode;
  /** Le texte du serveur, destiné à l'avocat. */
  message: string;
  /** Le dossier en cause, quand le refus en nomme un. */
  caseId?: string;
  /** Pour `case_change_refused` : le geste refusé, ou `attachment` pour une pièce jointe d'un autre dossier. */
  caseChange?: ConversationCaseGesture['kind'] | 'attachment';
}

const CASE_CHANGE_KINDS: ReadonlyArray<NonNullable<ConversationCaseRefusal['caseChange']>> = [
  'add_related',
  'remove_related',
  'remove_principal',
  'attachment',
];

/** Le refus porté par une trame `error`, ou `null` pour une autre erreur. */
export function readConversationCaseRefusal(payload: unknown, message: string): ConversationCaseRefusal | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const { code } = record;
  if (!isConversationCaseRefusalCode(code)) return null;
  const caseChange = CASE_CHANGE_KINDS.find((candidate) => candidate === record.case_change);
  return {
    code,
    message,
    ...(typeof record.case_id === 'string' ? { caseId: record.case_id } : {}),
    ...(caseChange ? { caseChange } : {}),
  };
}

/**
 * Les gestes d'un message refusé qui doivent repartir avec le suivant.
 *
 * Le serveur applique les gestes d'un message en une transaction : s'il en refuse
 * un, aucun n'est appliqué. Seul le geste refusé est retiré, sinon le message
 * renvoyé serait refusé sans fin ; les autres perdraient la demande de l'avocat.
 * Des gestes illisibles pour le serveur ne valent pas d'être renvoyés.
 */
export function caseChangesAfterRefusal<T extends ConversationCaseGesture>(
  changes: readonly T[],
  refusal: ConversationCaseRefusal,
): T[] {
  if (refusal.code === 'invalid_case_changes') return [];
  if (refusal.code !== 'case_change_refused') return [...changes];
  return changes.filter((change) => !(change.kind === refusal.caseChange && change.case_id === refusal.caseId));
}

/*
 * Gestes en attente d'un écran (Word, volet Outlook/Gmail). Le serveur reste seul
 * juge de la liste : l'écran n'affiche que ce que `conversation_cases` ou le
 * détail de la conversation lui a rendu. Les gestes de l'avocat ne sont écrits
 * qu'au message suivant, dans `case_changes` ; en attendant, ces fonctions les
 * rejouent sur la liste du serveur pour que l'écran montre ce que l'avocat vient
 * de faire.
 */

/**
 * Un geste en attente d'envoi. Le titre n'est pas dans le contrat du geste
 * (le serveur le relit en base) : il ne sert qu'à l'affichage entre le clic et
 * la trame `conversation_cases` qui suivra l'envoi.
 */
export type PendingCaseChange = ConversationCaseGesture & { title: string };

/**
 * Ajoute un geste à la file en annulant celui qu'il défait : retirer un
 * connexe qu'on vient d'ajouter (ou l'inverse) ne doit rien envoyer. Sans cela,
 * le serveur recevrait un ajout, avec son contrôle d'accès, pour un dossier
 * que l'avocat a déjà écarté, et pourrait refuser tout le message pour lui.
 */
export function queueCaseChange(pending: readonly PendingCaseChange[], change: PendingCaseChange): PendingCaseChange[] {
  const opposite = change.kind === 'add_related' ? 'remove_related' : change.kind === 'remove_related' ? 'add_related' : null;
  if (opposite) {
    const undone = pending.findIndex((entry) => entry.kind === opposite && entry.case_id === change.case_id);
    if (undone !== -1) return pending.filter((_, index) => index !== undone);
  }
  return [...pending, change];
}

/** Rejoue les gestes en attente sur la liste rendue par le serveur, dans l'ordre où le serveur les appliquera. */
export function applyPendingCaseChanges(
  cases: readonly ConversationCaseListItem[],
  pending: readonly PendingCaseChange[],
): ConversationCaseListItem[] {
  let result = [...cases];
  for (const change of pending) {
    if (change.kind === 'add_related') {
      if (!result.some((item) => item.case_id === change.case_id)) {
        result.push({ case_id: change.case_id, title: change.title, client_name: null, role: 'connexe', added_by: null });
      }
    } else {
      // `remove_related` et `remove_principal` sortent tous deux le dossier de la
      // liste active ; ce qui les distingue (le principal ne laisse aucun
      // connexe pour le remplacer) est déjà dans la liste serveur.
      result = result.filter((item) => item.case_id !== change.case_id);
    }
  }
  return result;
}

/** Le principal de la liste, s'il y en a un. */
export function principalOf(cases: readonly ConversationCaseListItem[]): ConversationCaseListItem | null {
  return cases.find((item) => item.role === 'principal') ?? null;
}

/**
 * Le `case_id` d'un message : le dossier choisi au composeur, seulement s'il diffère du
 * principal de la dernière liste rendue par le serveur (liste vide : envoyé). Sans ce
 * filtre, un écran resté ouvert rétablirait en silence un ancien principal, changé depuis
 * sur un autre écran. Un message sans dossier ne retire rien, et la trame qui le suit
 * recale la pastille.
 */
export function messageCaseId(
  chosenCaseId: string | null | undefined,
  serverCases: readonly ConversationCaseListItem[],
): string | undefined {
  if (!chosenCaseId) return undefined;
  return principalOf(serverCases)?.case_id === chosenCaseId ? undefined : chosenCaseId;
}

/**
 * Les dossiers à montrer en connexes, quand `principalId` est le dossier choisi
 * au composeur. Tout dossier de la liste autre que lui y figure : l'ancien
 * principal d'une conversation dont l'avocat vient de choisir un autre dossier
 * reste connexe, et un connexe choisi au composeur devient le principal, comme
 * le serveur le fera à l'envoi.
 */
export function relatedCasesFor(
  cases: readonly ConversationCaseListItem[],
  principalId: string | null,
): Array<{ id: string; title: string }> {
  return cases
    .filter((item) => item.case_id !== principalId)
    .map((item) => ({ id: item.case_id, title: item.title }));
}
